/**
 * Two common d6 cubes on a wooden dice tray (Three.js).
 * `rollTo` tumble-throws them onto the given faces (1–6).
 */

import * as THREE from 'three'
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'
import { DICE_ROLL_MS } from '@shared/index'
import { applyWebglBudget, webglOnPhone, webglRendererOptions } from '../webglPhone'
import './D10Table.css'

export const D10_ROLL_MS = DICE_ROLL_MS

const SIZE = 1.5
const FELT_TOP = -1.02
const REST_Y = FELT_TOP + SIZE / 2

const clamp01 = (t: number): number => Math.max(0, Math.min(1, t))
const easeOut = (t: number): number => 1 - (1 - t) ** 2.15
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t

/** Western d6: opposites sum to 7. Identity has 1 up, 2 toward +Z, 3 toward +X. */
const CUBE_FACES: { value: number; normal: THREE.Vector3; texUp: THREE.Vector3 }[] = [
  { value: 1, normal: new THREE.Vector3(0, 1, 0), texUp: new THREE.Vector3(0, 0, -1) },
  { value: 6, normal: new THREE.Vector3(0, -1, 0), texUp: new THREE.Vector3(0, 0, 1) },
  { value: 2, normal: new THREE.Vector3(0, 0, 1), texUp: new THREE.Vector3(0, 1, 0) },
  { value: 5, normal: new THREE.Vector3(0, 0, -1), texUp: new THREE.Vector3(0, 1, 0) },
  { value: 3, normal: new THREE.Vector3(1, 0, 0), texUp: new THREE.Vector3(0, 1, 0) },
  { value: 4, normal: new THREE.Vector3(-1, 0, 0), texUp: new THREE.Vector3(0, 1, 0) },
]

const PIP_CELLS: Record<number, [number, number][]> = {
  1: [[1, 1]],
  2: [[0, 0], [2, 2]],
  3: [[0, 0], [1, 1], [2, 2]],
  4: [[0, 0], [2, 0], [0, 2], [2, 2]],
  5: [[0, 0], [2, 0], [1, 1], [0, 2], [2, 2]],
  6: [[0, 0], [0, 1], [0, 2], [2, 0], [2, 1], [2, 2]],
}

interface FaceSettle {
  value: number
  quat: THREE.Quaternion
}

interface DieLook {
  color: number
  roughness: number
  metalness: number
  ink: 'dark' | 'light'
}

interface DieRig {
  group: THREE.Group
  body: THREE.MeshStandardMaterial
  numberMats: THREE.MeshBasicMaterial[]
  faces: FaceSettle[]
  rest: THREE.Vector3
}

const DIE_LOOKS: DieLook[] = [
  { color: 0xf3e6c8, roughness: 0.38, metalness: 0.12, ink: 'dark' },
  { color: 0xb71c1c, roughness: 0.34, metalness: 0.18, ink: 'light' },
  { color: 0x1565c0, roughness: 0.32, metalness: 0.22, ink: 'light' },
  { color: 0x1b5e20, roughness: 0.4, metalness: 0.14, ink: 'light' },
  { color: 0x4a148c, roughness: 0.34, metalness: 0.2, ink: 'light' },
  { color: 0x141414, roughness: 0.28, metalness: 0.28, ink: 'light' },
  { color: 0xe65100, roughness: 0.36, metalness: 0.16, ink: 'dark' },
  { color: 0x00695c, roughness: 0.34, metalness: 0.2, ink: 'light' },
  { color: 0xc9a227, roughness: 0.3, metalness: 0.48, ink: 'dark' },
  { color: 0xf7f1e3, roughness: 0.44, metalness: 0.08, ink: 'dark' },
  { color: 0x880e4f, roughness: 0.33, metalness: 0.18, ink: 'light' },
  { color: 0x37474f, roughness: 0.3, metalness: 0.36, ink: 'light' },
  { color: 0x4e342e, roughness: 0.42, metalness: 0.12, ink: 'light' },
  { color: 0x283593, roughness: 0.32, metalness: 0.22, ink: 'light' },
]

function roundedRectShape(w: number, h: number, r: number): THREE.Shape {
  const hw = w / 2
  const hh = h / 2
  const s = new THREE.Shape()
  s.moveTo(-hw + r, -hh)
  s.lineTo(hw - r, -hh)
  s.quadraticCurveTo(hw, -hh, hw, -hh + r)
  s.lineTo(hw, hh - r)
  s.quadraticCurveTo(hw, hh, hw - r, hh)
  s.lineTo(-hw + r, hh)
  s.quadraticCurveTo(-hw, hh, -hw, hh - r)
  s.lineTo(-hw, -hh + r)
  s.quadraticCurveTo(-hw, -hh, -hw + r, -hh)
  return s
}

function makeCanvasTexture(
  paint: (ctx: CanvasRenderingContext2D, size: number) => void,
  size = 256,
): THREE.CanvasTexture {
  const c = document.createElement('canvas')
  c.width = size
  c.height = size
  const ctx = c.getContext('2d')
  if (ctx) paint(ctx, size)
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  tex.anisotropy = 4
  return tex
}

function woodTexture(): THREE.CanvasTexture {
  return makeCanvasTexture((ctx, size) => {
    ctx.fillStyle = '#5c3218'
    ctx.fillRect(0, 0, size, size)
    for (let x = 0; x < size; x++) {
      const wave = Math.sin(x * 0.11) * 8 + Math.sin(x * 0.37) * 3
      const shade = 70 + ((x * 13) % 40) + wave
      ctx.fillStyle = `rgb(${shade + 28},${shade - 8},${shade - 32})`
      ctx.fillRect(x, 0, 1, size)
    }
    ctx.globalAlpha = 0.18
    for (let i = 0; i < 24; i++) {
      ctx.fillStyle = i % 2 ? '#2a1408' : '#c48a4a'
      ctx.fillRect(Math.random() * size, 0, 1 + Math.random() * 2, size)
    }
    ctx.globalAlpha = 1
  })
}

function feltTexture(): THREE.CanvasTexture {
  return makeCanvasTexture((ctx, size) => {
    ctx.fillStyle = '#1c3f48'
    ctx.fillRect(0, 0, size, size)
    for (let i = 0; i < 9000; i++) {
      const n = Math.random()
      const r = 18 + n * 36
      const g = 72 + n * 70
      const b = 68 + n * 55
      ctx.fillStyle = `rgba(${r},${g},${b},0.42)`
      ctx.fillRect(Math.random() * size, Math.random() * size, 2, 2)
    }
    ctx.globalAlpha = 0.2
    ctx.fillStyle = '#0a1c22'
    ctx.beginPath()
    ctx.ellipse(size * 0.5, size * 0.5, size * 0.46, size * 0.38, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.globalAlpha = 1
  })
}

function pipTexture(value: number, fill: string): THREE.CanvasTexture {
  const size = 256
  const c = document.createElement('canvas')
  c.width = size
  c.height = size
  const ctx = c.getContext('2d')
  if (ctx) {
    ctx.clearRect(0, 0, size, size)
    const cells = PIP_CELLS[value] ?? PIP_CELLS[1]
    const pad = size * 0.2
    const span = size - pad * 2
    const radius = size * 0.125
    for (const [cx, cy] of cells) {
      const x = pad + (cx / 2) * span
      const y = pad + (cy / 2) * span
      ctx.beginPath()
      ctx.arc(x + 3, y + 4, radius, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(0,0,0,0.28)'
      ctx.fill()
      ctx.beginPath()
      ctx.arc(x, y, radius, 0, Math.PI * 2)
      ctx.fillStyle = fill
      ctx.fill()
    }
  }
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = 4
  return tex
}

function settleQuat(
  normal: THREE.Vector3,
  texUp: THREE.Vector3,
): THREE.Quaternion {
  const up = new THREE.Vector3(0, 1, 0)
  const q = new THREE.Quaternion().setFromUnitVectors(
    normal.clone().normalize(),
    up,
  )
  const tip = texUp.clone().applyQuaternion(q)
  const xz = new THREE.Vector3(tip.x, 0, tip.z)
  if (xz.lengthSq() < 1e-6) return q
  xz.normalize()
  const yaw = Math.atan2(xz.x, xz.z) - Math.PI
  const qYaw = new THREE.Quaternion().setFromAxisAngle(up, -yaw)
  return qYaw.multiply(q)
}

function orientPlane(
  plane: THREE.Mesh,
  normal: THREE.Vector3,
  texUp: THREE.Vector3,
): void {
  const n = normal.clone().normalize()
  const xAxis = new THREE.Vector3().crossVectors(texUp, n).normalize()
  const yAxis = new THREE.Vector3().crossVectors(n, xAxis).normalize()
  const m = new THREE.Matrix4().makeBasis(xAxis, yAxis, n)
  plane.quaternion.setFromRotationMatrix(m)
}

function buildDie(inks: THREE.CanvasTexture[]): DieRig {
  const body = new THREE.MeshStandardMaterial({
    color: 0xf3e6c8,
    roughness: 0.38,
    metalness: 0.12,
  })
  const mesh = new THREE.Mesh(
    new RoundedBoxGeometry(SIZE, SIZE, SIZE, 4, 0.16),
    body,
  )
  mesh.castShadow = true
  mesh.receiveShadow = true

  const group = new THREE.Group()
  group.add(mesh)

  const settle: FaceSettle[] = []
  const numberMats: THREE.MeshBasicMaterial[] = []
  const half = SIZE / 2
  const pipSize = SIZE * 0.72
  for (const face of CUBE_FACES) {
    const mat = new THREE.MeshBasicMaterial({
      map: inks[face.value],
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
    const plane = new THREE.Mesh(new THREE.PlaneGeometry(pipSize, pipSize), mat)
    plane.position.copy(face.normal).multiplyScalar(half + 0.012)
    orientPlane(plane, face.normal, face.texUp)
    group.add(plane)
    numberMats.push(mat)
    settle.push({
      value: face.value,
      quat: settleQuat(face.normal, face.texUp),
    })
  }

  return {
    group,
    body,
    numberMats,
    faces: settle,
    rest: new THREE.Vector3(),
  }
}

export class D10Table {
  private readonly root: HTMLDivElement
  private readonly canvas: HTMLCanvasElement
  private readonly renderer: THREE.WebGLRenderer
  private readonly scene: THREE.Scene
  private readonly camera: THREE.PerspectiveCamera
  private readonly left: DieRig
  private readonly right: DieRig
  private readonly darkInk: THREE.CanvasTexture[]
  private readonly lightInk: THREE.CanvasTexture[]
  private readonly resizeObs: ResizeObserver
  private lastColors: [number, number] = [0, 0]
  private readonly idleAxisL = new THREE.Vector3(0.25, 1, 0.12).normalize()
  private readonly idleAxisR = new THREE.Vector3(-0.18, 1, 0.28).normalize()
  private idle = false
  private idleAngle = 0
  private lastFrame = 0
  private raf = 0
  private disposed = false

  constructor(host: HTMLElement) {
    this.root = document.createElement('div')
    this.root.className = 'd10-root'
    this.canvas = document.createElement('canvas')
    this.canvas.className = 'd10-canvas'
    this.root.append(this.canvas)
    host.appendChild(this.root)

    this.renderer = new THREE.WebGLRenderer(webglRendererOptions(this.canvas))
    applyWebglBudget(this.renderer, 2)
    const shadows = !webglOnPhone()
    this.renderer.shadowMap.enabled = shadows
    if (shadows) this.renderer.shadowMap.type = THREE.PCFShadowMap
    this.scene = new THREE.Scene()
    this.camera = new THREE.PerspectiveCamera(34, 1, 0.1, 40)
    this.frameCamera(1)

    const ambient = new THREE.AmbientLight(0xfff1dc, 0.55)
    const hemi = new THREE.HemisphereLight(0xffe6c4, 0x1a2430, 0.45)
    const key = new THREE.DirectionalLight(0xfff4e4, 1.55)
    key.position.set(3.2, 7.2, 4.4)
    key.castShadow = !webglOnPhone()
    key.shadow.mapSize.set(1024, 1024)
    key.shadow.camera.near = 0.5
    key.shadow.camera.far = 22
    key.shadow.camera.left = -7
    key.shadow.camera.right = 7
    key.shadow.camera.top = 5
    key.shadow.camera.bottom = -5
    const fill = new THREE.PointLight(0x7aa2ff, 0.5, 18)
    fill.position.set(-4, 2.4, 3.2)
    const rim = new THREE.PointLight(0xffc978, 0.4, 14)
    rim.position.set(0, 1.2, -3.5)
    this.scene.add(ambient, hemi, key, fill, rim)

    this.darkInk = []
    this.lightInk = []
    for (let v = 1; v <= 6; v++) {
      this.darkInk[v] = pipTexture(v, '#1a1208')
      this.lightInk[v] = pipTexture(v, '#f4ead4')
    }

    this.addTray()

    this.left = buildDie(this.darkInk)
    this.right = buildDie(this.darkInk)
    this.left.rest.set(-1.55, REST_Y, 0)
    this.right.rest.set(1.55, REST_Y, 0)
    this.left.group.position.copy(this.left.rest)
    this.right.group.position.copy(this.right.rest)
    this.scene.add(this.left.group, this.right.group)
    this.paintDice()

    this.resizeObs = new ResizeObserver(() => this.resize())
    this.resizeObs.observe(this.root)
    this.resize()
    this.loop()
  }

  /**
   * Slow tumble above the felt while the player has not rolled yet.
   * Skipped when the OS asks for reduced motion.
   */
  startIdle(): void {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    this.idle = !reduce
    this.idleAngle = 0
  }

  stopIdle(): void {
    this.idle = false
  }

  /**
   * Throws both dice onto `leftValue` and `rightValue` (1–6).
   */
  rollTo(leftValue: number, rightValue: number): Promise<void> {
    this.idle = false
    const duration = D10_ROLL_MS
    const started = performance.now()
    const leftStart = this.left.group.quaternion.clone()
    const rightStart = this.right.group.quaternion.clone()
    const leftEnd = this.faceQuat(this.left, leftValue)
    const rightEnd = this.faceQuat(this.right, rightValue)
    const leftA = this.randomAxis()
    const rightA = this.randomAxis()
    const leftB = this.randomAxis()
    const rightB = this.randomAxis()
    const leftSpins = 2.5 + Math.random() * 1.5
    const rightSpins = 2.5 + Math.random() * 1.5
    const leftSpinB = 1.2 + Math.random() * 1.2
    const rightSpinB = 1.2 + Math.random() * 1.2
    const leftFrom = this.throwOrigin(this.left.rest, -1)
    const rightFrom = this.throwOrigin(this.right.rest, 1)

    return new Promise((resolve) => {
      const step = (now: number) => {
        if (this.disposed) {
          resolve()
          return
        }
        const t = clamp01((now - started) / duration)
        const land = easeOut(t)
        const spinKeep = 1 - t ** 1.35
        this.tumble(
          this.left,
          leftStart,
          leftEnd,
          leftA,
          leftB,
          leftSpins,
          leftSpinB,
          leftFrom,
          land,
          spinKeep,
          t,
        )
        this.tumble(
          this.right,
          rightStart,
          rightEnd,
          rightA,
          rightB,
          rightSpins,
          rightSpinB,
          rightFrom,
          land,
          spinKeep,
          t,
        )
        if (t < 1) requestAnimationFrame(step)
        else {
          this.settleDie(this.left, leftEnd)
          this.settleDie(this.right, rightEnd)
          resolve()
        }
      }
      requestAnimationFrame(step)
    })
  }

  dispose(): void {
    this.disposed = true
    cancelAnimationFrame(this.raf)
    this.resizeObs.disconnect()
    const materials = new Set<THREE.Material>()
    const maps = new Set<THREE.Texture>()
    this.scene.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return
      obj.geometry.dispose()
      const list = Array.isArray(obj.material) ? obj.material : [obj.material]
      for (const mat of list) materials.add(mat)
    })
    for (const mat of materials) {
      const mapped = mat as THREE.MeshStandardMaterial
      if (mapped.map) maps.add(mapped.map)
      mat.dispose()
    }
    for (const map of maps) map.dispose()
    try {
      this.renderer.forceContextLoss()
    } catch {
      /* Already lost. */
    }
    this.renderer.dispose()
    this.root.remove()
  }

  private addTray(): void {
    const woodMap = woodTexture()
    woodMap.repeat.set(2.2, 1.4)
    const feltMap = feltTexture()
    feltMap.repeat.set(2.4, 1.5)

    const wood = new THREE.MeshStandardMaterial({
      map: woodMap,
      color: 0x8d4c24,
      roughness: 0.62,
      metalness: 0.08,
    })
    const darkWood = new THREE.MeshStandardMaterial({
      map: woodMap,
      color: 0x4a2412,
      roughness: 0.7,
      metalness: 0.06,
    })
    const felt = new THREE.MeshStandardMaterial({
      map: feltMap,
      color: 0x2a6b62,
      roughness: 0.94,
      metalness: 0,
    })
    const gold = new THREE.MeshStandardMaterial({
      color: 0xe0c265,
      roughness: 0.28,
      metalness: 0.82,
    })
    const brass = new THREE.MeshStandardMaterial({
      color: 0xb08638,
      roughness: 0.36,
      metalness: 0.72,
    })

    const base = new THREE.Mesh(new THREE.BoxGeometry(7.85, 0.42, 4.85), wood)
    base.position.y = -1.44
    base.castShadow = true
    base.receiveShadow = true
    this.scene.add(base)

    const apron = new THREE.Mesh(new THREE.BoxGeometry(8.15, 0.22, 5.15), darkWood)
    apron.position.y = -1.68
    apron.receiveShadow = true
    this.scene.add(apron)

    const well = roundedRectShape(6.58, 3.58, 0.34)
    const feltGeo = new THREE.ExtrudeGeometry(well, {
      depth: 0.08,
      bevelEnabled: false,
      curveSegments: 14,
    })
    feltGeo.rotateX(-Math.PI / 2)
    const feltMesh = new THREE.Mesh(feltGeo, felt)
    feltMesh.position.y = -1.1
    feltMesh.receiveShadow = true
    this.scene.add(feltMesh)

    const outer = roundedRectShape(7.55, 4.55, 0.52)
    const inner = roundedRectShape(6.58, 3.58, 0.34)
    outer.holes.push(inner)
    const rimGeo = new THREE.ExtrudeGeometry(outer, {
      depth: 0.52,
      bevelEnabled: true,
      bevelThickness: 0.045,
      bevelSize: 0.045,
      bevelSegments: 2,
      curveSegments: 16,
    })
    rimGeo.rotateX(-Math.PI / 2)
    const rim = new THREE.Mesh(rimGeo, wood)
    rim.position.y = -1.1
    rim.castShadow = true
    rim.receiveShadow = true
    this.scene.add(rim)

    const goldOuter = roundedRectShape(7.42, 4.42, 0.48)
    const goldInner = roundedRectShape(7.08, 4.08, 0.4)
    goldOuter.holes.push(goldInner)
    const goldGeo = new THREE.ExtrudeGeometry(goldOuter, {
      depth: 0.055,
      bevelEnabled: true,
      bevelThickness: 0.015,
      bevelSize: 0.012,
      bevelSegments: 1,
      curveSegments: 16,
    })
    goldGeo.rotateX(-Math.PI / 2)
    const goldLip = new THREE.Mesh(goldGeo, gold)
    goldLip.position.y = -0.56
    goldLip.castShadow = true
    this.scene.add(goldLip)

    const legGeo = new THREE.CylinderGeometry(0.16, 0.2, 0.4, 10)
    const studGeo = new THREE.SphereGeometry(0.11, 12, 10)
    const legPos: [number, number][] = [
      [-3.35, -1.9],
      [3.35, -1.9],
      [-3.35, 1.9],
      [3.35, 1.9],
    ]
    for (const [x, z] of legPos) {
      const leg = new THREE.Mesh(legGeo, darkWood)
      leg.position.set(x, -1.78, z)
      leg.castShadow = true
      this.scene.add(leg)
      const stud = new THREE.Mesh(studGeo, brass)
      stud.position.set(x * 1.02, -0.5, z * 1.02)
      this.scene.add(stud)
    }
  }

  private paintDice(): void {
    const leftLook = this.pickLook([this.lastColors[0], this.lastColors[1]])
    const rightLook = this.pickLook([
      leftLook.color,
      this.lastColors[0],
      this.lastColors[1],
    ])
    this.applyLook(this.left, leftLook)
    this.applyLook(this.right, rightLook)
    this.lastColors = [leftLook.color, rightLook.color]
  }

  private pickLook(avoid: number[]): DieLook {
    const pool = DIE_LOOKS.filter((look) => !avoid.includes(look.color))
    const list = pool.length > 0 ? pool : DIE_LOOKS
    const index = Math.floor(Math.random() * list.length)
    return list[index] ?? DIE_LOOKS[0]!
  }

  private applyLook(die: DieRig, look: DieLook): void {
    die.body.color.setHex(look.color)
    die.body.roughness = look.roughness
    die.body.metalness = look.metalness
    const bank = look.ink === 'light' ? this.lightInk : this.darkInk
    for (let i = 0; i < die.numberMats.length; i++) {
      const mat = die.numberMats[i]
      const value = die.faces[i]?.value ?? i + 1
      mat.map = bank[value] ?? null
      mat.needsUpdate = true
    }
  }

  private tumble(
    die: DieRig,
    from: THREE.Quaternion,
    to: THREE.Quaternion,
    axisA: THREE.Vector3,
    axisB: THREE.Vector3,
    spinsA: number,
    spinsB: number,
    origin: THREE.Vector3,
    land: number,
    spinKeep: number,
    t: number,
  ): void {
    die.group.quaternion.slerpQuaternions(from, to, land)
    die.group.quaternion.premultiply(
      new THREE.Quaternion().setFromAxisAngle(
        axisA,
        spinKeep * spinsA * Math.PI * 2,
      ),
    )
    die.group.quaternion.multiply(
      new THREE.Quaternion().setFromAxisAngle(
        axisB,
        spinKeep * spinsB * Math.PI * 2,
      ),
    )
    const bounce = Math.abs(Math.sin(t * Math.PI * 2.2)) * (1 - t) ** 1.35 * 0.95
    die.group.position.set(
      lerp(origin.x, die.rest.x, land),
      lerp(origin.y, die.rest.y, land) + bounce,
      lerp(origin.z, die.rest.z, land),
    )
  }

  private settleDie(die: DieRig, quat: THREE.Quaternion): void {
    die.group.quaternion.copy(quat)
    die.group.position.copy(die.rest)
  }

  private throwOrigin(rest: THREE.Vector3, side: number): THREE.Vector3 {
    return new THREE.Vector3(
      rest.x + side * (0.15 + Math.random() * 0.35),
      2.55 + Math.random() * 0.9,
      2.05 + Math.random() * 0.55,
    )
  }

  private faceQuat(die: DieRig, value: number): THREE.Quaternion {
    const face = die.faces.find((f) => f.value === value) ?? die.faces[0]
    if (!face) return new THREE.Quaternion()
    return face.quat.clone()
  }

  private randomAxis(): THREE.Vector3 {
    return new THREE.Vector3(
      Math.random() - 0.5,
      0.2 + Math.random() * 0.55,
      Math.random() - 0.5,
    ).normalize()
  }

  /** Wide short viewports (landscape phones) push the tray onto the names. */
  private frameCamera(aspect: number): void {
    if (aspect >= 2.4) {
      this.camera.fov = 30
      this.camera.position.set(0, 6.1, 8.1)
      this.camera.lookAt(0, -1.05, 0.15)
      return
    }
    this.camera.fov = 34
    this.camera.position.set(0, 5.35, 6.7)
    this.camera.lookAt(0, -0.4, 0)
  }

  private resize(): void {
    const w = Math.max(1, this.root.clientWidth)
    const h = Math.max(1, this.root.clientHeight)
    const aspect = w / h
    this.camera.aspect = aspect
    this.frameCamera(aspect)
    this.camera.updateProjectionMatrix()
    applyWebglBudget(this.renderer, 2)
    this.renderer.setSize(w, h, false)
  }

  private tickIdle(dt: number): void {
    this.idleAngle += dt
    const bob = Math.sin(this.idleAngle * 2.1) * 0.05
    const hover = 0.22
    this.left.group.position.set(
      this.left.rest.x,
      this.left.rest.y + hover + bob,
      this.left.rest.z,
    )
    this.right.group.position.set(
      this.right.rest.x,
      this.right.rest.y + hover - bob,
      this.right.rest.z,
    )
    this.left.group.rotateOnWorldAxis(this.idleAxisL, dt * 1.15)
    this.right.group.rotateOnWorldAxis(this.idleAxisR, dt * 0.95)
  }

  private loop = (): void => {
    if (this.disposed) return
    this.raf = requestAnimationFrame(this.loop)
    const now = performance.now()
    const dt = this.lastFrame ? Math.min(0.05, (now - this.lastFrame) / 1000) : 0
    this.lastFrame = now
    if (this.idle) this.tickIdle(dt)
    this.renderer.render(this.scene, this.camera)
  }
}
